import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import {
  type User,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { getFirebaseAuth } from '../lib/firebase';
import type { SessionScope } from '@mbbe/config';
import { opsSession } from '../lib/sessions';
import { getOpsMe, type OpsMe } from './lib/opsApi';
import { pruneExpiredFinishNoteDrafts } from './lib/storage';
import { opsTaskDrafts } from './lib/taskActionDrafts';

interface OpsAuthValue {
  user: User | null;
  me: OpsMe | null;
  isLoading: boolean;
  isStaff: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const OpsAuthContext = createContext<OpsAuthValue | null>(null);

export const SESSION_SUPERSEDED = 'SESSION_SUPERSEDED';

const profileSignature = (profile: OpsMe) => `${profile.role ?? ''}:${profile.active !== false}`;

export function OpsAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [me, setMe] = useState<OpsMe | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStaff, setIsStaff] = useState(false);
  // Identity binding (replaces a boolean "sign-in handled" flag): the scope a
  // profile request was started for, and the last role/active state committed.
  const loadStartedRef = useRef<SessionScope | null>(null);
  const committedRef = useRef<{ uid: string | null; signature: string } | null>(null);

  const clearAccountState = () => {
    committedRef.current = null;
    setMe(null);
    setIsStaff(false);
  };

  // A role/active change for the same account rotates the scope so data
  // cached under the previous access level is dropped and re-requested.
  const settleScope = (scope: SessionScope, profile: OpsMe): SessionScope => {
    const signature = profileSignature(profile);
    const previous = committedRef.current;
    committedRef.current = { uid: scope.uid, signature };
    if (!previous || previous.uid !== scope.uid || previous.signature === signature) return scope;
    const rotated = opsSession.begin(scope.uid);
    loadStartedRef.current = rotated;
    return rotated;
  };

  useEffect(() => {
    // Expired unfinished drafts are removed; other accounts' live drafts are kept.
    pruneExpiredFinishNoteDrafts();
    let auth;
    try {
      auth = getFirebaseAuth();
    } catch {
      opsSession.end();
      setUser(null);
      clearAccountState();
      setIsLoading(false);
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      const uid = nextUser?.uid ?? null;
      // The sign-in flow (or an earlier event) already owns this identity.
      if (uid !== null && opsSession.current.uid === uid && loadStartedRef.current === opsSession.current) return;

      const scope = opsSession.ensure(uid);
      setIsLoading(true);
      setUser(nextUser);
      clearAccountState();
      if (!nextUser) {
        // The staff session ended: unsent task input and in-flight task requests die with it (#166).
        opsTaskDrafts.reset();
        setIsLoading(false);
        return;
      }

      loadStartedRef.current = scope;
      let owner = scope;
      try {
        const profile = await getOpsMe();
        if (!scope.isCurrent()) return;
        owner = settleScope(scope, profile);
        setMe(profile);
        setIsStaff(profile.active !== false);
      } catch {
        if (!scope.isCurrent()) return;
        clearAccountState();
      }
      if (owner.isCurrent()) setIsLoading(false);
    });
    return () => {
      unsubscribe();
      opsSession.end();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    let auth;
    try {
      auth = getFirebaseAuth();
    } catch {
      throw new Error('FIREBASE_CONFIG_ERROR');
    }

    let credential;
    try {
      credential = await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      throw new Error('FIREBASE_AUTH_ERROR:' + ((err as { code?: string })?.code || 'unknown'));
    }

    // Starting B's scope aborts A's requests and drops A's cached data
    // synchronously, before B's state is rendered.
    const scope = opsSession.begin(credential.user.uid);
    loadStartedRef.current = scope;
    clearAccountState();
    setUser(credential.user);

    const abandon = async () => {
      await firebaseSignOut(auth);
      // Another account may have signed in while the sign-out was in flight.
      if (opsSession.current.uid !== null && !scope.isCurrent()) return;
      opsSession.ensure(null);
      setUser(null);
      clearAccountState();
      setIsLoading(false);
    };

    try {
      const profile = await getOpsMe();
      if (!scope.isCurrent()) throw new Error(SESSION_SUPERSEDED);
      if (profile.active === false) {
        await abandon();
        throw new Error('NOT_OPS_STAFF');
      }
      const owner = settleScope(scope, profile);
      setMe(profile);
      setIsStaff(true);
      if (owner.isCurrent()) setIsLoading(false);
    } catch (err) {
      if (err instanceof Error && (err.message === 'NOT_OPS_STAFF' || err.message === SESSION_SUPERSEDED)) throw err;
      // A request cancelled by a newer session must not sign the newer account out.
      if (!scope.isCurrent()) throw new Error(SESSION_SUPERSEDED);
      await abandon();
      throw new Error('OPS_CHECK_FAILED');
    }
  };

  const signOut = async () => {
    let auth;
    try {
      auth = getFirebaseAuth();
    } catch {
      opsSession.end();
      opsTaskDrafts.reset();
      setUser(null);
      clearAccountState();
      return;
    }

    const previous = opsSession.current;
    // Drop the account's requests and cached data before the network sign-out.
    opsSession.end();
    try {
      await firebaseSignOut(auth);
    } catch (err) {
      if (opsSession.current.uid === null && previous.uid) opsSession.begin(previous.uid);
      throw err;
    }
    opsTaskDrafts.reset();
    setUser(null);
    clearAccountState();
  };

  return (
    <OpsAuthContext.Provider value={{ user, me, isLoading, isStaff, signIn, signOut }}>
      {children}
    </OpsAuthContext.Provider>
  );
}

export function useOpsAuth(): OpsAuthValue {
  const context = useContext(OpsAuthContext);
  if (!context) throw new Error('useOpsAuth must be used within an OpsAuthProvider');
  return context;
}
